/**
 * Privacy Policy and Terms of Service.
 *
 * TRANSCRIBED from the live qost.ai pages (/privacy and /terms, both
 * "Last updated: March 1, 2025") so the rebuilt site does not lose them.
 *
 * ⚠️  Verify before launch: this is a copy of published legal text. Diff it
 * against your canonical source (and have counsel confirm) before this becomes
 * the live version. Legal wording should never be edited by hand in a rebuild
 * without that check.
 */

export type LegalBlock =
  | { type: "p"; text: string }
  | { type: "ul"; items: string[] };

export type LegalSection = {
  heading: string;
  blocks: LegalBlock[];
};

export type LegalDocument = {
  title: string;
  updated: string;
  intro: string;
  sections: LegalSection[];
  contactHeading: string;
  contactIntro: string;
  contact: { label: string; value: string }[];
  contactOutro?: string;
};

export const privacyPolicy: LegalDocument = {
  title: "Privacy Policy",
  updated: "March 1, 2025",
  intro:
    "At Qost.ai, protecting your data is fundamental to everything we do. This policy explains what we collect, how we use it, and your rights as a user.",
  sections: [
    {
      heading: "1. Information We Collect",
      blocks: [
        {
          type: "p",
          text: "We collect information you provide directly to us, such as when you create an account, fill out a form, or contact us for support. This includes:",
        },
        {
          type: "ul",
          items: [
            "Account information: name, email address, company name, and job title.",
            "Billing information: processed securely through our payment provider; we do not store full card numbers.",
            "Cloud usage data: read-only metadata from your connected AWS accounts (billing records, resource identifiers, usage metrics). We never access the content of your workloads or data stored in cloud services.",
            "Usage data: pages visited, features used, and actions taken within the Qost.ai platform.",
            "Communications: messages you send us via email or our contact form.",
          ],
        },
      ],
    },
    {
      heading: "2. How We Use Your Information",
      blocks: [
        { type: "p", text: "We use the information we collect to:" },
        {
          type: "ul",
          items: [
            "Provide, operate, and improve the Qost.ai platform and services.",
            "Generate cost optimization recommendations and savings reports.",
            "Send you service-related notifications, updates, and security alerts.",
            "Respond to your comments, questions, and customer support requests.",
            "Detect, investigate, and prevent fraudulent transactions and other illegal activities.",
            "Comply with legal obligations and enforce our Terms of Service.",
            "Conduct analytics to understand how our services are used and to improve them.",
          ],
        },
      ],
    },
    {
      heading: "3. Data Sharing and Disclosure",
      blocks: [
        {
          type: "p",
          text: "We do not sell, trade, or otherwise transfer your personal information to third parties for marketing purposes. We may share your information with:",
        },
        {
          type: "ul",
          items: [
            "Service providers: trusted third parties who assist us in operating our platform (e.g., cloud hosting, analytics, customer support tools), bound by confidentiality obligations.",
            "Cloudlaya, Inc.: as Qost.ai is a product of Cloudlaya, certain operational data may be shared with Cloudlaya's engineering and support teams to maintain and improve the service.",
            "Legal requirements: when required by law, court order, or governmental authority.",
            "Business transfers: in connection with a merger, acquisition, or sale of assets, with appropriate confidentiality protections.",
          ],
        },
      ],
    },
    {
      heading: "4. Data Security",
      blocks: [
        {
          type: "p",
          text: "We implement industry-standard technical and organizational measures to protect your information, including:",
        },
        {
          type: "ul",
          items: [
            "Encryption in transit (TLS 1.2+) and at rest (AES-256).",
            "SOC 2 Type II compliance program covering security, availability, and confidentiality.",
            "ISO 27001-aligned information security management practices.",
            "Strict access controls and least-privilege principles for our team.",
            "Regular security assessments and penetration testing.",
          ],
        },
        {
          type: "p",
          text: "Despite these measures, no security system is impenetrable. We encourage you to use strong, unique passwords and to notify us immediately if you believe your account has been compromised.",
        },
      ],
    },
    {
      heading: "5. Cloud Account Access",
      blocks: [
        {
          type: "p",
          text: "Qost.ai connects to your AWS account using strictly read-only IAM permissions. This means:",
        },
        {
          type: "ul",
          items: [
            "We can read billing data, usage metrics, and resource configurations.",
            "We cannot create, modify, or delete any resources in your AWS environment.",
            "We cannot access the content of your S3 buckets, databases, or application data.",
            "You can revoke our access at any time by deleting the IAM role from your AWS account.",
          ],
        },
        {
          type: "p",
          text: "All cloud credentials are encrypted at rest using industry-standard key management services.",
        },
      ],
    },
    {
      heading: "6. Data Retention",
      blocks: [
        {
          type: "p",
          text: "We retain your personal information for as long as your account is active or as needed to provide our services. Specifically:",
        },
        {
          type: "ul",
          items: [
            "Account data is retained for the duration of your subscription and up to 30 days after account closure.",
            "Cloud usage and billing data is retained for up to 24 months to enable trend analysis and historical reporting.",
            "Support communications are retained for up to 3 years for quality assurance purposes.",
          ],
        },
        {
          type: "p",
          text: "You may request deletion of your data at any time by contacting us at privacy@qost.ai.",
        },
      ],
    },
    {
      heading: "7. Your Rights",
      blocks: [
        {
          type: "p",
          text: "Depending on your location, you may have the following rights regarding your personal data:",
        },
        {
          type: "ul",
          items: [
            "Access: request a copy of the personal data we hold about you.",
            "Correction: request correction of inaccurate or incomplete data.",
            "Deletion: request that we delete your personal data, subject to certain legal exceptions.",
            "Portability: receive your data in a structured, machine-readable format.",
            "Objection: object to certain types of processing, including direct marketing.",
            "Restriction: request that we restrict processing of your data in certain circumstances.",
          ],
        },
        {
          type: "p",
          text: "To exercise any of these rights, please contact us at privacy@qost.ai. We will respond within 30 days.",
        },
      ],
    },
    {
      heading: "8. Cookies and Tracking",
      blocks: [
        { type: "p", text: "We use cookies and similar tracking technologies to:" },
        {
          type: "ul",
          items: [
            "Maintain your session and authentication state.",
            "Remember your preferences and settings.",
            "Analyze usage patterns to improve our platform.",
            "Provide relevant content and features.",
          ],
        },
        {
          type: "p",
          text: "You can control cookie settings through your browser. Disabling certain cookies may affect platform functionality. We do not use cookies for cross-site advertising purposes.",
        },
      ],
    },
    {
      heading: "9. Third-Party Links",
      blocks: [
        {
          type: "p",
          text: "Our platform may contain links to third-party websites, including Cloudlaya.com and partner sites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.",
        },
      ],
    },
    {
      heading: "10. Changes to This Policy",
      blocks: [
        {
          type: "p",
          text: "We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a prominent notice on our platform at least 30 days before the changes take effect. Your continued use of Qost.ai after the effective date constitutes acceptance of the updated policy.",
        },
      ],
    },
    {
      heading: "11. Contact Us",
      blocks: [
        {
          type: "p",
          text: "If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:",
        },
      ],
    },
  ],
  contactHeading: "11. Contact Us",
  contactIntro:
    "If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:",
  contact: [
    { label: "Entity", value: "Qost.ai — a product of Cloudlaya, Inc." },
    { label: "Email", value: "privacy@qost.ai" },
    { label: "Address", value: "Kathmandu, Nepal" },
  ],
  contactOutro:
    "You may also reach our parent company, Cloudlaya, at hello@cloudlaya.com or via the contact form at cloudlaya.com.",
};

export const termsOfService: LegalDocument = {
  title: "Terms of Service",
  updated: "March 1, 2025",
  intro:
    "Please read these Terms carefully before using Qost.ai. By using our platform, you agree to be bound by these Terms.",
  sections: [
    {
      heading: "1. Acceptance of Terms",
      blocks: [
        {
          type: "p",
          text: 'By accessing or using Qost.ai (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of a company or other legal entity, you represent that you have authority to bind that entity to these Terms.',
        },
        {
          type: "p",
          text: "If you do not agree to these Terms, do not access or use the Service. These Terms apply to all users of the Service, including free, trial, and paid subscribers.",
        },
      ],
    },
    {
      heading: "2. Description of Service",
      blocks: [
        {
          type: "p",
          text: "Qost.ai is a cloud cost optimization platform operated by Cloudlaya, Inc. The Service provides:",
        },
        {
          type: "ul",
          items: [
            "AI-driven analysis of your AWS billing and usage data.",
            "Identification of cost savings opportunities across compute, storage, and networking.",
            "Real-time monitoring and anomaly detection for unexpected spend.",
            "Recommendations for Reserved Instances, Savings Plans, and resource rightsizing.",
            "Reports and dashboards for cloud cost visibility.",
          ],
        },
        {
          type: "p",
          text: 'The Service is provided "as is" and features may change or be discontinued at any time with reasonable notice.',
        },
      ],
    },
    {
      heading: "3. Account Registration and Security",
      blocks: [
        {
          type: "p",
          text: "To use the Service, you must create an account by providing accurate, complete, and current information. You are responsible for:",
        },
        {
          type: "ul",
          items: [
            "Maintaining the confidentiality of your account credentials.",
            "All activities that occur under your account.",
            "Notifying us immediately at security@qost.ai of any unauthorized access.",
          ],
        },
        {
          type: "p",
          text: "You may not share your account with others or create accounts using automated means. We reserve the right to terminate accounts found to be in violation of these Terms.",
        },
      ],
    },
    {
      heading: "4. Cloud Account Access and Permissions",
      blocks: [
        {
          type: "p",
          text: "To provide the Service, Qost.ai requires read-only access to your AWS account via an IAM Role. By connecting your cloud account, you:",
        },
        {
          type: "ul",
          items: [
            "Grant Qost.ai permission to read billing data, usage metrics, and resource configurations.",
            "Confirm that you have the authority to grant such access to your AWS account.",
            "Acknowledge that we will not make any modifications to your cloud resources.",
          ],
        },
        {
          type: "p",
          text: "You may revoke this access at any time by removing the IAM role from your AWS account. Revoking access will limit the functionality of the Service.",
        },
      ],
    },
    {
      heading: "5. Acceptable Use",
      blocks: [
        {
          type: "p",
          text: "You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:",
        },
        {
          type: "ul",
          items: [
            "Attempt to gain unauthorized access to any portion of the Service or related systems.",
            "Use the Service to store or transmit malicious code.",
            "Interfere with or disrupt the integrity or performance of the Service.",
            "Circumvent any security features, access controls, or usage limits.",
            "Reverse engineer, decompile, or disassemble the Service.",
            "Resell, sublicense, or redistribute the Service without our express written consent.",
            "Use the Service in violation of any applicable laws or regulations.",
          ],
        },
      ],
    },
    {
      heading: "6. Subscription Plans and Billing",
      blocks: [
        { type: "p", text: "Qost.ai offers free and paid subscription plans. By subscribing to a paid plan:" },
        {
          type: "ul",
          items: [
            "You authorize us to charge your payment method at the applicable subscription rate.",
            "Subscriptions renew automatically at the end of each billing period unless cancelled.",
            "All fees are in USD and are non-refundable except as required by law.",
            "Price changes will be communicated at least 30 days in advance.",
          ],
        },
        {
          type: "p",
          text: "You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period.",
        },
      ],
    },
    {
      heading: "7. Intellectual Property",
      blocks: [
        {
          type: "p",
          text: "The Service and all content, features, and functionality — including software, algorithms, text, graphics, and logos — are owned by Cloudlaya, Inc. and are protected by intellectual property laws.",
        },
        {
          type: "p",
          text: "These Terms do not grant you any right, title, or interest in the Service beyond the limited license to use it in accordance with these Terms. You may not copy, modify, create derivative works of, or exploit any part of the Service without our prior written consent.",
        },
      ],
    },
    {
      heading: "8. Data Ownership",
      blocks: [
        {
          type: "p",
          text: "You retain full ownership of your cloud data. By using the Service, you grant Qost.ai a limited, non-exclusive license to access and process your cloud metadata solely for the purpose of providing the Service.",
        },
        {
          type: "p",
          text: "We do not claim ownership of any data you upload, connect, or generate through use of the Service. We will not use your data to train AI models for third parties or share it with competitors.",
        },
      ],
    },
    {
      heading: "9. Confidentiality",
      blocks: [
        {
          type: "p",
          text: "Each party may disclose confidential information to the other in connection with the Service. Each party agrees to:",
        },
        {
          type: "ul",
          items: [
            "Protect the other's confidential information using at least the same degree of care used for its own confidential information.",
            "Not disclose confidential information to third parties without prior written consent.",
            "Use confidential information only for the purposes of performing obligations under these Terms.",
          ],
        },
        {
          type: "p",
          text: "This obligation does not apply to information that is publicly available or independently developed.",
        },
      ],
    },
    {
      heading: "10. Disclaimers and Limitation of Liability",
      blocks: [
        {
          type: "p",
          text: 'THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.',
        },
        {
          type: "p",
          text: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLOUDLAYA, INC. AND QOST.AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUE, ARISING OUT OF YOUR USE OF THE SERVICE.",
        },
        {
          type: "p",
          text: "OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS SHALL NOT EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICE IN THE THREE MONTHS PRECEDING THE CLAIM.",
        },
        {
          type: "p",
          text: "Cost savings recommendations are estimates based on available data. Actual savings may vary. We do not guarantee specific cost reductions.",
        },
      ],
    },
    {
      heading: "11. Indemnification",
      blocks: [
        {
          type: "p",
          text: "You agree to indemnify, defend, and hold harmless Cloudlaya, Inc., its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, or expenses (including reasonable attorney's fees) arising from:",
        },
        {
          type: "ul",
          items: [
            "Your use of the Service in violation of these Terms.",
            "Your violation of any applicable law or regulation.",
            "Any content or data you submit through the Service.",
            "Your infringement of any third-party intellectual property rights.",
          ],
        },
      ],
    },
    {
      heading: "12. Termination",
      blocks: [
        {
          type: "p",
          text: "Either party may terminate these Terms at any time. We reserve the right to suspend or terminate your account immediately if:",
        },
        {
          type: "ul",
          items: [
            "You violate these Terms or our Acceptable Use Policy.",
            "You fail to pay applicable fees.",
            "We are required to do so by law.",
          ],
        },
        {
          type: "p",
          text: "Upon termination, your right to use the Service will immediately cease. We will delete your data within 30 days of account closure, unless retention is required by law.",
        },
      ],
    },
    {
      heading: "13. Governing Law and Disputes",
      blocks: [
        {
          type: "p",
          text: "These Terms are governed by the laws of Nepal, without regard to conflict of law principles. Any dispute arising from these Terms shall be resolved through binding arbitration, except that either party may seek injunctive relief in a court of competent jurisdiction.",
        },
        {
          type: "p",
          text: "If you are a consumer in the European Union, nothing in these Terms affects your statutory rights under applicable EU consumer protection law.",
        },
      ],
    },
    {
      heading: "14. Changes to These Terms",
      blocks: [
        {
          type: "p",
          text: "We may revise these Terms from time to time. We will notify you of material changes by email or through a prominent in-app notice at least 30 days before they take effect. Your continued use of the Service after the effective date of revised Terms constitutes your acceptance.",
        },
      ],
    },
    {
      heading: "15. Contact Information",
      blocks: [
        { type: "p", text: "Questions about these Terms? Contact us at:" },
      ],
    },
  ],
  contactHeading: "15. Contact Information",
  contactIntro: "Questions about these Terms? Contact us at:",
  contact: [
    { label: "Entity", value: "Qost.ai — a product of Cloudlaya, Inc." },
    { label: "Email", value: "legal@qost.ai" },
    { label: "Address", value: "Kathmandu, Nepal" },
    { label: "Website", value: "https://www.cloudlaya.com/" },
  ],
};
